import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useGetReleaseOrderDetailsQuery } from '@/store/api/saleReleaseOrderApi'
import { DIALOG_CONTENT_PADDING, HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: frmCtrlSalesQryROAdd_upgrade_detail (btnDetails "Release
// Details"). READ-ONLY datasheet of container detail rows for a release order
// (release_no). Columns order from the detail subform; NW/GW/Bag Standard
// format (2dp, right-aligned) with a totals row. BE sorts by delivery_order,
// container_no. Mirrors DoPrintDialog shell but renders an MUI Table (not an
// iframe).

const DISPLAY_DATE_FORMAT = 'DD-MMM-YYYY'

const fmtNum = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return ''
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v)
}

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return ''
  const d = dayjs(v)
  return d.isValid() ? d.format(DISPLAY_DATE_FORMAT) : v
}

export default function ReleaseDetailsDialog({
  open,
  releaseNo,
  onClose,
}: {
  open: boolean
  releaseNo: string | null
  onClose: () => void
}) {
  const { data, isFetching } = useGetReleaseOrderDetailsQuery(releaseNo ?? '', {
    skip: releaseNo === null,
  })

  const rows = data?.items ?? []
  const totalNw = rows.reduce((sum, r) => sum + (r.nwRelease ?? 0), 0)
  const totalGw = rows.reduce((sum, r) => sum + (r.gwRelease ?? 0), 0)
  const totalBag = rows.reduce((sum, r) => sum + (r.bagRelease ?? 0), 0)

  const numCellSx = { textAlign: 'right', whiteSpace: 'nowrap' } as const
  const headSx = { fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.paper' } as const

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        component="span"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}
      >
        <Box
          component="span"
          title={releaseNo ? `Release Details (${releaseNo})` : 'Release Details'}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
        >
          Release Details
          {releaseNo ? (
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>{` (${releaseNo})`}</Box>
          ) : null}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={DIALOG_CONTENT_PADDING}>
        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={40} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
            No release details found.
          </Typography>
        ) : (
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Delivery Order</TableCell>
                  <TableCell sx={headSx}>Container No.</TableCell>
                  <TableCell sx={headSx}>Invoice No.</TableCell>
                  <TableCell sx={headSx}>ICO No.</TableCell>
                  <TableCell sx={headSx}>Package</TableCell>
                  <TableCell sx={{ ...headSx, textAlign: 'right' }}>NW Release</TableCell>
                  <TableCell sx={{ ...headSx, textAlign: 'right' }}>GW Release</TableCell>
                  <TableCell sx={{ ...headSx, textAlign: 'right' }}>Bag Release</TableCell>
                  <TableCell sx={headSx}>Person Updated</TableCell>
                  <TableCell sx={headSx}>Date Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.deliveryOrder ?? ''}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.containerNo ?? ''}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.invoiceNo ?? ''}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.icoNo ?? ''}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.package ?? ''}</TableCell>
                    <TableCell sx={numCellSx}>{fmtNum(r.nwRelease)}</TableCell>
                    <TableCell sx={numCellSx}>{fmtNum(r.gwRelease)}</TableCell>
                    <TableCell sx={numCellSx}>{fmtNum(r.bagRelease)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.personUpdated ?? ''}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.dateUpdated)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: 'text.primary' }}
                  >
                    Total
                  </TableCell>
                  <TableCell sx={{ ...numCellSx, fontWeight: 700, color: 'text.primary' }}>
                    {fmtNum(totalNw)}
                  </TableCell>
                  <TableCell sx={{ ...numCellSx, fontWeight: 700, color: 'text.primary' }}>
                    {fmtNum(totalGw)}
                  </TableCell>
                  <TableCell sx={{ ...numCellSx, fontWeight: 700, color: 'text.primary' }}>
                    {fmtNum(totalBag)}
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableFooter>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
